import { inject } from '@angular/core';
import {
  CanActivateChildFn,
  CanActivateFn,
  NavigationError,
  Router,
} from '@angular/router';
import {
  NewVersionReloadService,
  navigationTarget,
} from './new-version-reload.service';
import { NewVersionService } from './new-version.service';

/**
 * Turns a page change into a full load of its target while a new frontend
 * build is known. Angular runs every `canDeactivate` first, so this only sees
 * navigations the user has already confirmed leaving for, and it never sees
 * query-only changes or clicks on the current page, which run no guards.
 *
 * Keep it out of a `canActivate` array that holds another guard: guards in one
 * array run concurrently, so the load could cut off, say, a token refresh in
 * the auth guard. In `canActivateChild` it runs after the parent's guards.
 */
export const reloadIntoNewVersion: CanActivateFn & CanActivateChildFn = (
  _route,
  state
) => {
  const reload = inject(NewVersionReloadService);
  if (reload.locked()) {
    return false;
  }
  const navigation = inject(Router).getCurrentNavigation();
  if (!navigation) {
    return true;
  }
  return !reload.reloadInsteadOf(
    navigation.id,
    navigationTarget(navigation, state.url)
  );
};

/**
 * A page whose code a deploy removed fails to load after its guards passed.
 * If a check then confirms a new build, the tab loads that page in it;
 * otherwise the navigation fails as before. Returning nothing keeps the
 * router's own handling: it restores the address and rejects the navigation.
 *
 * The page the user stayed on keeps working while the check runs. If they
 * have navigated on by the time it answers, the failed target is stale and
 * loading it would drop what they did since, unasked; the notice and their
 * next page change take over instead.
 */
export function reloadOnFailedPageLoad(error: NavigationError): void {
  const reload = inject(NewVersionReloadService);
  const target = reload.targetOfPassedNavigation(error.id);
  if (!target || reload.locked()) {
    return;
  }
  const router = inject(Router);
  const settled = router.lastSuccessfulNavigation;
  void inject(NewVersionService)
    .checkNow()
    .then((result) => {
      const movedOn =
        router.lastSuccessfulNavigation !== settled ||
        router.getCurrentNavigation() !== null;
      if (result === 'different' && !movedOn) {
        reload.requestAutomaticReload(target);
      }
    });
}
